<template>
  <draggable
    tag="el-collapse"
    v-model="trackList"
    :component-data="getComponentData()"
    handle=".drag"
    @end="handleMoveEnd"
  >
    <el-collapse-item
      v-for="(track,index) in trackList"
      :key="track.tempId"
      :name="track.tempId"
      @click.native="handleOpen(track)"
      class="track-item"
    >
      <template slot="title">
        <div class="header">
          <div class="order" @click.stop.prevent>{{ index + 1 }}</div>
          <el-tooltip placement="top" effect="dark" :content="track.trackName">
            <div class="name text-ellipsis" @click.stop.prevent>{{ track.trackName }}</div>
          </el-tooltip>

          <!-- 上传中 -->
          <div class="process" v-if="!track.mediaFinish || !track.media" @click.stop.prevent>
            <el-progress :percentage="track.uploadPercent" :show-text="false" class="progress"></el-progress>
            <span v-if="track.uploadPercent === 100">音频检测中..</span>
          </div>

          <!-- 音频异常 -->
          <div v-if="!track.uploadPercent && track.hasError" @click.stop.prevent>
            <span class="error">音频存在严重音质问题, 请重新上传</span>
            <check-icon :track="{fileCheck: track.media.checkQuality}"></check-icon>
          </div>

          <!-- 歌手名 -->
          <div v-if="track.mediaFinish && !track.hasError" class="singer text-ellipsis" @click.stop.prevent>
            {{ track.trackSingerList.map(singer => singer.singerName).join('、') }}
          </div>

          <!-- 替换音频 -->
          <verify-uploader
            v-if="!track.uploadPercent"
            class="replace"
            action="/action/upload?type=track"
            :accept="`.${trackType.join(',.')}`"
            :before-upload="handleMediaBefore(track)"
            :on-progress="handleMediaProgress(track)"
            :on-success="handleMediaSuccess(track)"
            :on-error="handleMediaError(track)"
            :show-file-list="false"
            :disabled="track.lockBasic"
          >
            <el-button type="text" :disabled="track.lockBasic">替换音频</el-button>
          </verify-uploader>

          <!-- 试听 -->
          <online-listen
            v-if="track.media && !track.uploadPercent"
            :media="track.media"
            :track-name="track.trackName"
            class="prelisten"
            @click.stop.prevent
          ></online-listen>

          <span v-if="!track.trackId && !track.uploadPercent" class="delete" @click.stop="handleDelete(index)">删除</span>
          <el-button v-if="track.trackId" type="text" class="flow" @click.stop="handleShowFlow(track)">分发进度</el-button>
        </div>
        <svg-icon icon-class="drag" class="drag" @click.native.stop.prevent></svg-icon>
        <span class="detail">详情</span>
      </template>

      <el-form
        v-if="!album.albumId || showMap[track.tempId]"
        ref="trackForms"
        :disabled="disabledEdit(track)"
        :rules="createRules(track)"
        :model="track"
        :inline-message="true"
        :validate-on-rule-change="false"
        label-width="100px"
      >
        <song-item
          :ref="`songItems_${track.tempId}`"
          :song="track"
          :album="album"
          @sync-cp="handleSyncCp"
          @sync-pay="handleSyncPayment"
        ></song-item>
      </el-form>
    </el-collapse-item>

    <el-dialog
      v-if="showFlow"
      title="分发进度"
      append-to-body
      width="400px"
      :close-on-click-modal="false"
      :visible.sync="showFlow"
      @close="handleCloseFlow"
    >
      <flow
        :status="album.status === 3 || album.status === 4 ? album.status : trackStatusToAlbumStatus[flowTrack.status]"
        :bu-trace="flowTrack.buTrace"
        @cancel="handleCloseFlow"
      ></flow>
    </el-dialog>
  </draggable>
</template>

<script>
import draggable from 'vuedraggable'
import cloneDeep from 'lodash-es/cloneDeep'
import VerifyUploader from '@/components/VerifyUploader'
import CheckIcon from '@/components/AudioCheckIcon'
import OnlineListen from '@/components/OnlineListen'
import { validateFileExt } from '@/utils/validate'
import { isAudioCheckError } from '@/utils/fileUtil'
import SongItem from './SongItem'
import Flow from '@/views/track/components/Flow'
import { trackType, trackStatusToAlbumStatus } from '../mixin'
import { mapGetters } from 'vuex'

export default {
  name: 'SongList',
  components: {
    draggable,
    SongItem,
    CheckIcon,
    OnlineListen,
    VerifyUploader,
    Flow,
  },
  props: {
    songs: {
      type: Array,
      default() {
        return []
      }
    },
    album: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      trackStatusToAlbumStatus,
      activeNames: [],
      trackList: [],
      showMap: {}, // 记录打开了哪些面板, 打开过的面板才渲染,减少整体渲染时间
      rules: {
        trackName: [
          { required: true, message: '请输入歌曲名', trigger: 'blur' },
        ],
        genre: [
          { required: true, message: '请填写一级流派', trigger: 'change' }
        ],
        'trackExtend.subGenre': [
          { required: true, message: '请填写二级流派', trigger: 'change' }
        ],
        trackSingerList: [
          { required: true, message: '至少含有一个艺人', trigger: 'blur' }
        ],
        language: [
          { type: 'number', required: true, message: '请选择语言', trigger: 'change' }
        ],
        cdIndex: [
          { type: 'number', required: true, message: '请选择CD', trigger: 'change' }
        ],
        saleTime: [
          { required: true, message: '请选择上线时间', trigger: 'blur' }
        ],
        publicTime: [
          { required: true, message: '请选择发行时间', trigger: 'blur' }
        ],
        'trackCopyright.date': [
          { type: 'array', required: true, message: '请选择授权时间', trigger: 'change' }
        ],
        'trackCopyright.cpSource': [
          { type: 'number', required: true, message: '请选择授权来源', trigger: 'change' }
        ],
        'trackCopyright.copyrightArea': [
          { type: 'array', required: true, message: '请选择授权区域', trigger: 'change' }
        ],
        'trackCopyright.isOnly': [
          { type: 'number', required: true, message: '请选择授权形式', trigger: 'change' }
        ],
        'trackCopyright.isOverseaOnly': [
          { type: 'number', required: true, message: '请选择是否海外总代理', trigger: 'change' }
        ],
        'trackCopyright.canAuth': [
          { type: 'number', required: true, message: '请选择是否可转授', trigger: 'change' }
        ],
        'trackCopyright.isAgent': [
          { type: 'number', required: true, message: '请选择授权关系', trigger: 'change' }
        ],
        'trackPayment.paymentMode': [
          { type: 'number', required: true, message: '请选择付费类型', trigger: 'change' }
        ],
      },

      showFlow: false,
      flowTrack: null
    }
  },
  computed: {
    ...mapGetters([
      'copyrightCompanyList'
    ]),
    trackType() {
      return trackType
    }
  },
  watch: {

    trackList: {
      deep: true,
      handler(newVal) {
        this.$emit('update:songs', newVal)
      }
    },
    songs(newVal) {
      this.trackList = newVal
    }
  },
  created() {
    this.trackList = this.songs
  },
  methods: {
    disabledEdit(track) {
      if (!this.$route.query.id || track.create) {
        return false
      }
      const { copyrightCompanyList } = this
      return track.trackCopyright.cpStatus !== 1 ||
        !track.trackCopyright.cpCompany ||
        !copyrightCompanyList.find(item => item.cpCompanyId === track.trackCopyright.cpCompany.companyId)
    },
    createRules(track) {
      const validFileList = (rule, value, callback) => {
        if (value.length === 0 && track.audioFileList.length === 0 && !track.lockCopyright) {
          callback(new Error('必须上传录音文件/表演者文件之一'))
        }
        callback()
      }
      return {
        ...this.rules,
        actorFileList: [
          { validator: validFileList, trigger: 'change' }
        ]
      }
    },

    inputChanged(value) {
      this.activeNames = value
    },

    getComponentData() {
      return {
        on: {
          input: this.inputChanged
        },
        attrs: {
          wrap: true
        },
        props: {
          value: this.activeNames
        }
      }
    },

    /**
     * 检查所有歌曲信息合法性
     * 1. 音频是否上传完成 并且 没有严重音质问题
     * 2. 信息填写是否完整
     */
    async validate() {
      const trackList = this.trackList

      if (trackList.length === 0) throw new Error('专辑至少包含一首歌曲')

      const hasUnfinish = trackList.find(trackObj => !trackObj.mediaFinish)
      if (hasUnfinish) throw new Error(`歌曲【${hasUnfinish.trackName}】音频文件未上传完成, 请完成后提交`)

      const hasErrorMedia = trackList.find(trackObj => trackObj.hasError)
      if (hasErrorMedia) throw new Error(`歌曲【${hasErrorMedia.trackName}】音频文件存在严重问题, 请重新上传`)

      // 针对新增的, 歌曲编辑 collapse-item 没打开过的 歌曲, 需要单独对数据做校验, 因为组件没渲染, 不会触发 form 的 validate
      for (const trackObj of trackList.filter(item => !item.trackId && !item.hasOpend)) {
        if (!trackObj.trackCopyright.copyrightArea?.length || !trackObj.trackCopyright.date.length) {
          this.handleOpen(trackObj)
          this.activeNames = [trackObj.tempId] // 设置这个让 collapse-item 自动打开
          throw new Error(`歌曲【${trackObj.trackName}】信息不完整`)
        }
      }

      let currentForm
      if (this.$refs.trackForms) {
        try {
          for (const formComp of this.$refs.trackForms) {
            currentForm = formComp
            await formComp.validate()
          }
        } catch (error) {
          const trackData = currentForm.$options.propsData.model
          this.activeNames = [trackData.tempId] // 设置这个让 collapse-item 自动打开
          throw new Error(`歌曲【${trackData.trackName}】信息不完整`)
        }
      }
    },

    getData() {
      const tl = cloneDeep(this.trackList)
      for (let i = 0; i < tl.length; i++) {
        const track = tl[i]

        if (this.$refs[`songItems_${track.tempId}`] && this.$refs[`songItems_${track.tempId}`].length) {
          const refSongItem = this.$refs[`songItems_${track.tempId}`][0]
          track.lyricData = refSongItem.getLyricData()

          const insData = refSongItem.$refs.inst.getData()
          const tempObj = {}
          insData
            .filter(item => item.value)
            .forEach(item => {
              tempObj[item.key] = item.value
            })

          track.trackInstrument = tempObj
        }
      }
      return tl
    },

    /**
     * 拖动事件结束后，其实数组中的元素顺序已经改变，现在只需要改变 location 值
     */
    handleMoveEnd(evt) {
      this.trackList.forEach((item, index) => {
        item.location = index + 1
      })
    },

    checkDup(file) {
      if (this.trackList.find(item => item.md5 === file.md5)) return false

      return true
    },

    handleShowFlow(track) {
      this.flowTrack = track
      this.showFlow = true
    },

    handleCloseFlow() {
      this.showFlow = false
      this.flowTrack = null
    },

    /**
     * @param {Object} - trackObj
     */
    handleOpen(trackObj) {
      if (!this.showMap[trackObj.tempId]) {
        this.$set(this.showMap, trackObj.tempId, true)
        trackObj.hasOpend = true // 标记这首歌的 tab 打开过了
      }
    },

    handleMediaProgress(trackObj) {
      return (event, file, fileList) => {
        trackObj.uploadPercent = event.percent
      }
    },

    /**
     * @param {Object} file 和其他 file 对象属性一样,多了 md5 这个值
     */
    handleMediaBefore(trackObj) {
      return (file) => {
        if (!validateFileExt(trackType, file.name)) {
          this.$message.warning('不支持的文件类型')
          return Promise.reject()
        }

        if (!this.checkDup(file)) {
          this.$message({
            message: '不能上传重复歌曲',
            type: 'error'
          })
          return Promise.reject()
        }

        trackObj.mediaFinish = false
      }
    },

    handleMediaError(trackObj) {
      return (error, file, fileList) => {
        console.error(error)
        this.$message({
          type: 'error',
          message: `<${file.name}>上传失败，请稍后重试`
        })
        trackObj.mediaFinish = true
      }
    },

    handleMediaSuccess(trackObj) {
      return (resp, file, fileList) => {
        const respData = resp.data
        trackObj.mediaFinish = true // 表示音频上传完成, 无论有没严重错误
        trackObj.hasError = isAudioCheckError(respData.file_info.audio_check)
        trackObj.md5 = respData.md5
        trackObj.uploadPercent = 0
        trackObj.media = {
          mediaUrl: respData.url,
          mediaPath: respData.file_id,
          using: 1,
          remarks: this.fileRemarks,
          fileType: file.name.slice(file.name.lastIndexOf('.') + 1),
          fileSize: file.size,
          fileName: file.name,
          md5: respData.md5,
          duration: respData.file_info.duration != null ? respData.file_info.duration : 0,
          checkQuality: respData.file_info.audio_check,
        }
      }
    },

    handleDelete(index) {
      this.$confirm('所有填写的资料将会丢失！确认删除？', '注意', {
        type: 'warning'
      }).then(_ => {
        this.trackList.splice(index, 1)
      }).catch(_ => {})
    },

    handleSyncCp(copyrightKey, copyrightData, copyrightTo) {
      const { trackList } = this

      if (copyrightKey === 'trackCopyright') {
        for (let i = 1; i < trackList.length; i++) { // 第一个不用复制
          const trackObj = trackList[i]
          const newData = cloneDeep(copyrightData)
          newData.id = trackObj[copyrightKey].id
          trackObj[copyrightKey] = newData
          if (copyrightTo) {
            trackObj.trackCopyrightCompetitor.copyrightTo = copyrightTo
          }
        }
        return
      }

      for (let i = 1; i < trackList.length; i++) { // 第一个不用复制
        const trackObj = trackList[i]
        const newData = cloneDeep(copyrightData)
        trackObj[copyrightKey] = []

        for (const cp of newData) {
          cp.id = void (0)
          trackObj[copyrightKey].push(cp)
        }
      }
    },

    handleSyncPayment(paymentData) {
      const { trackList } = this
      for (let i = 1; i < trackList.length; i++) { // 第一个不用复制
        const trackObj = trackList[i]

        // 必须解锁，不然被同步的歌曲付费变动无法进入审核
        trackObj.lockBasic = false

        const newData = cloneDeep(paymentData)
        newData.id = trackObj.trackPayment ? trackObj.trackPayment.id : void (0)
        newData.track = trackObj.trackId
        trackObj.trackPayment = newData
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/styles/variables.scss";

.track-item {
  position: relative;
  border: 1px solid #e8e8e8;
  margin-bottom: 0;

  &:hover {
    box-shadow: 0 0 4px rgba(0,0,0,.09);
  }
  .header {
    position: relative;
    display: flex;
    width: calc(100% - 100px);
    cursor: default;
  }
  .order {
    width: 50px;
    text-align: center;
  }
  .process {
    width: 200px;
    font-size: 12px;

    .progress {
      display: inline-block;
      width: 130px;
    }
  }
  .error {
    color: $danger;
  }
  .name {
    width: 200px;
  }

  .singer {
    width: 200px;
  }

  .drag, .delete, .detail, .flow, .replace, .prelisten {
    position: absolute;
    color: #0060ff;
  }
  .delete {
    right: 8px;
    cursor: pointer;
  }
  .flow {
    right: 8px;
    top: 12px;
  }
  .detail {
    right: 65px;
  }
  .prelisten {
    right: 80px;
  }
  .drag {
    right: 15px;
    cursor: move;
  }
  .replace {
    right: 145px;
  }

  .el-collapse-item__header {
    flex-wrap: wrap;
    height: 50px;
    line-height: 50px;
    padding-left: 0;
    border-bottom: none;
    background-color: #f0f2f5;
  }
  .el-collapse-item__arrow {
    position: absolute;
    right: 20px;
    width: 54px;
    text-align: center;
    transform: rotate(90deg);
    cursor: pointer;
    color: #0060ff;

    &.is-active {
      transform: rotate(-90deg);
    }
  }
  .el-collapse-item__content {
    padding: 16px;
  }
  .collapse-id {
    width: 100%;
    padding-left: 8px;
    line-height: 1.5em;
  }
  .el-form-item__content {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
